import React, { FC, useEffect, useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { Stack, Typography } from '@mui/material';
import { subscriptionRoleService } from '../../services/subscriptionRoleService';
import { ISubscriptionRoleResult, ISubscriptionSearchTerms } from '../../types/subscription-role';
import { useAlert } from '../../providers/AlertProvider';
import { useTranslation } from 'react-i18next';
import SubscriptionSearchBar from '../../components/UserSearch/SubscriptionSearchBar';
import SubscriptionRoleResultsDataGrid from '../../components/UserSearch/SubscriptionRoleResultsDataGrid';
import { AxiosError } from 'axios';
import { useCursorPagination } from '../../hooks/useCursorPagination';

export const SubscriptionSearch: FC = () => {
  const [subscriptionSearchParams, setSubscriptionSearchParams] =
    useState<ISubscriptionSearchTerms | null>(null);
  const [subscriptionSearchResults, setSubscriptionSearchResults] = useState<
    ISubscriptionRoleResult[] | null
  >(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionTotalCount, setSubscriptionTotalCount] = useState(0);
  const subscriptionPagination = useCursorPagination(10);

  const alert = useAlert();
  const { t } = useTranslation();

  const handleSubscriptionSearch = async (subscriptionId: string) => {
    if (!subscriptionId) return;

    setSubscriptionLoading(true);
    setSubscriptionSearchResults(null);

    await subscriptionRoleService
      .getIdentitiesBySubscriptionId(
        subscriptionId,
        subscriptionPagination.cursor,
        subscriptionPagination.pageSize,
        'Forward'
      )
      .then((results) => {
        setSubscriptionSearchResults([...results.items]);
        setSubscriptionTotalCount(results.totalCount);
        if (results.items.length === 0) {
          alert.setErrorAlert('No users found for this subscription ID', 5);
        }
      })
      .catch((err: unknown) => {
        if (err instanceof AxiosError) {
          alert.setErrorAlert(t('alertMessages.userSearch.onError'));
        } else {
          alert.setErrorAlert('Failed to search by subscription ID');
        }
      });
    setSubscriptionLoading(false);
  };

  useEffect(() => {
    if (subscriptionSearchParams?.subscriptionId) {
      handleSubscriptionSearch(subscriptionSearchParams.subscriptionId);
    }
    if (!subscriptionSearchParams?.subscriptionId) {
      setSubscriptionSearchResults(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionSearchParams, subscriptionPagination.cursor, subscriptionPagination.pageSize]);

  return (
    <AuthenticatedTemplate>
      <Typography variant="MHHeading">Subscription search</Typography>
      <Typography variant="MHCaption" mb={3}>
        Look up users by subscription ID on the Subscription Role Service (SRS).
      </Typography>
      <Stack direction="column">
        <SubscriptionSearchBar
          subscriptionSearchParams={subscriptionSearchParams}
          setSubscriptionSearchParams={setSubscriptionSearchParams}
          loading={subscriptionLoading}
        />
        {subscriptionSearchResults && subscriptionSearchResults.length > 0 && (
          <>
            <Typography variant="MHHeading" mt={3} mb={2}>
              Results
            </Typography>
            <SubscriptionRoleResultsDataGrid
              searchResults={subscriptionSearchResults}
              loading={subscriptionLoading}
              totalCount={subscriptionTotalCount}
              page={subscriptionPagination.page}
              pageSize={subscriptionPagination.pageSize}
              onPageChange={(newPage) =>
                subscriptionPagination.handlePageChange(newPage, subscriptionSearchResults)
              }
              onPageSizeChange={subscriptionPagination.handlePageSizeChange}
            />
          </>
        )}
      </Stack>
    </AuthenticatedTemplate>
  );
};
